import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  MenuItem,
  Button,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import dayjs from "dayjs";
import { axiosInstance, useClientInfo } from "../utils/commonImports";

const GSTReport = () => {
  const navigate = useNavigate();
  const { clientId } = useClientInfo();

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [gstReport, setGstReport] = useState("output");
  const [viewType, setViewType] = useState("report");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("none");

  const [clientInfo, setClientInfo] = useState(null);

  /* ======================================================
     BACK BUTTON
     Goes to the previous page. If there is no history
     (page opened directly), goes to the Reports dashboard.
  ====================================================== */
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/reports");
    }
  };

  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId) return;

      try {
        const res = await axiosInstance.get(`/api/clients/${clientId}`);
        console.log("CLIENT INFO:", res.data);

        setClientInfo(res.data.client);
      } catch (err) {
        console.error("Client Fetch Error:", err);
      }
    };

    fetchClient();
  }, [clientId]);

  /* ======================================================
     LOAD DATA
  ====================================================== */

  const handleLoad = async () => {
    try {
      if (!clientId) return;

      setLoading(true);

      let url = "";

      switch (gstReport) {
        case "output":
          url = "/api/gst/output";
          break;
        case "rcm-sales":
          url = "/api/gst/rcm-sales";
          break;
        case "input":
          url = "/api/gst/input";
          break;
        case "rcm-purchase":
          url = "/api/gst/rcm-purchase";
          break;
        case "combined":
          url = "/api/gst/summary";
          break;
        default:
          url = "/api/gst/output";
      }

      const res = await axiosInstance.get(url, {
        params: {
          client_id: clientId,
          fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
          toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
        },
      });

      setData(res.data);
    } catch (err) {
      console.error("GST Load Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const groupCustomerWise = (rows) => {
    const grouped = {};

    rows.forEach((row) => {
      const key =
        row.customername || row.party_name || row.vendorname || "Unknown";

      if (!grouped[key]) grouped[key] = [];

      grouped[key].push(row);
    });

    return grouped;
  };

  const getReportTitle = () => {
    switch (gstReport) {
      case "output":
        return "GST OUTPUT REPORT (SALES)";
      case "rcm-sales":
        return "GST RCM REPORT (SALES)";
      case "input":
        return "GST INPUT REPORT (PURCHASE)";
      case "rcm-purchase":
        return "GST RCM REPORT (PURCHASE)";
      case "combined":
        return "GST COMBINED REPORT";
      default:
        return "GST REPORT";
    }
  };

  const getDateRange = () => {
    const from = fromDate ? dayjs(fromDate).format("DD/MM/YYYY") : "All";
    const to = toDate ? dayjs(toDate).format("DD/MM/YYYY") : "All";

    return `From: ${from}  To: ${to}`;
  };

  /* ======================================================
     TABLE RENDER
  ====================================================== */

  const renderTable = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0)
      return <Typography>No Records Found</Typography>;

    const isGrouped = groupBy === "customer";
    const groupedData = isGrouped ? groupCustomerWise(rows) : null;

    const renderRows = (list) =>
      list.map((row, index) => (
        <TableRow key={index}>
          <TableCell>
            {(row.invoice_number || row.purchase_number)?.trim()}
          </TableCell>

          <TableCell align="center">
            {row.invoice_date || row.purchase_date
              ? dayjs(row.invoice_date || row.purchase_date).format(
                  "DD/MM/YYYY",
                )
              : "-"}
          </TableCell>

          <TableCell>
            {row.customername || row.party_name || row.vendorname || "-"}
          </TableCell>

          <TableCell>
            {row.customergstin || row.gstin || row.vendorgstin || "-"}
          </TableCell>

          <TableCell align="center">{row.place_of_supply || "-"}</TableCell>

          <TableCell align="center">
            {Number(row.cgst_percent || 0).toFixed(2)}
          </TableCell>

          <TableCell align="center">
            {Number(row.sgst_percent || 0).toFixed(2)}
          </TableCell>

          <TableCell align="center">
            {Number(row.igst_percent || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.taxable_value || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.cgst || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.sgst || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.igst || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.total || 0).toFixed(2)}
          </TableCell>
        </TableRow>
      ));

    return (
      <TableContainer
        sx={{
          maxHeight: "65vh",
          overflow: "auto",
          border: "1px solid #bbb",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: 1250,
            "& th": {
              border: "1px solid #bbb",
              fontWeight: 700,
              fontSize: "0.70rem",
              backgroundColor: "#f3f4f6",
              textAlign: "center",
            },
            "& td": {
              border: "1px solid #ddd",
              fontSize: "0.70rem",
              padding: "4px 6px",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80, minWidth: 80 }}>No</TableCell>

              <TableCell sx={{ width: 70, minWidth: 70, textAlign: "center" }}>
                Date
              </TableCell>

              <TableCell sx={{ width: 180, minWidth: 180 }}>Party</TableCell>

              <TableCell sx={{ width: 90, minWidth: 90, textAlign: "center" }}>
                GSTIN
              </TableCell>

              <TableCell sx={{ width: 90, minWidth: 90, textAlign: "center" }}>
                Place of Supply
              </TableCell>

              <TableCell sx={{ width: 50, minWidth: 50, textAlign: "left" }}>
                CGST%
              </TableCell>

              <TableCell sx={{ width: 50, minWidth: 50, textAlign: "left" }}>
                SGST%
              </TableCell>

              <TableCell sx={{ width: 50, minWidth: 50, textAlign: "left" }}>
                IGST%
              </TableCell>

              <TableCell align="right" sx={{ width: 90, minWidth: 90 }}>
                Taxable
              </TableCell>

              <TableCell align="right" sx={{ width: 70, minWidth: 70 }}>
                CGST Amt
              </TableCell>

              <TableCell align="right" sx={{ width: 70, minWidth: 70 }}>
                SGST Amt
              </TableCell>

              <TableCell align="right" sx={{ width: 70, minWidth: 70 }}>
                IGST Amt
              </TableCell>

              <TableCell align="right" sx={{ width: 90, minWidth: 90 }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isGrouped
              ? Object.entries(groupedData).map(([customer, list]) => {
                  const subtotal = list.reduce(
                    (a, r) => {
                      a.taxable += Number(r.taxable_value || 0);
                      a.cgst += Number(r.cgst || 0);
                      a.sgst += Number(r.sgst || 0);
                      a.igst += Number(r.igst || 0);
                      a.total += Number(r.total || 0);
                      return a;
                    },
                    { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 },
                  );

                  return (
                    <React.Fragment key={customer}>
                      {/* CUSTOMER HEADER */}
                      <TableRow sx={{ backgroundColor: "#e0f2fe" }}>
                        <TableCell colSpan={13}>
                          <strong>{customer}</strong>
                        </TableCell>
                      </TableRow>

                      {renderRows(list)}

                      {/* CUSTOMER SUBTOTAL */}
                      <TableRow sx={{ backgroundColor: "#fef3c7" }}>
                        <TableCell colSpan={8} align="right">
                          <strong>Customer Total</strong>
                        </TableCell>

                        <TableCell align="right">
                          <strong>{subtotal.taxable.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{subtotal.cgst.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{subtotal.sgst.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{subtotal.igst.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{subtotal.total.toFixed(2)}</strong>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              : renderRows(rows)}

            {/* GRAND TOTAL */}
            <TableRow sx={{ backgroundColor: "#e5e7eb" }}>
              <TableCell colSpan={8} align="right">
                <strong>Grand Total</strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows
                    .reduce((a, r) => a + Number(r.taxable_value || 0), 0)
                    .toFixed(2)}
                </strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows.reduce((a, r) => a + Number(r.cgst || 0), 0).toFixed(2)}
                </strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows.reduce((a, r) => a + Number(r.sgst || 0), 0).toFixed(2)}
                </strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows.reduce((a, r) => a + Number(r.igst || 0), 0).toFixed(2)}
                </strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows
                    .reduce((a, r) => a + Number(r.total || 0), 0)
                    .toFixed(2)}
                </strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  /* ======================================================
     JSON VIEW
  ====================================================== */

  const renderJSON = () => (
    <pre
      style={{
        fontSize: 12,
        background: "#111",
        color: "#00ff88",
        padding: 15,
        overflow: "auto",
        maxHeight: "70vh",
      }}
    >
      {JSON.stringify(
        {
          gst_report: gstReport,
          report_type: viewType,
          from_date: fromDate ? dayjs(fromDate).format("DD/MM/YYYY") : null,
          to_date: toDate ? dayjs(toDate).format("DD/MM/YYYY") : null,
          data: data,
        },
        null,
        2,
      )}
    </pre>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box p={1}>
        <Paper sx={{ p: 1.5, mb: 1.5, border: "1px solid #ccc" }}>
          {/* HEADER WITH BACK BUTTON */}
          <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
            <Tooltip title="Back">
              <IconButton
                onClick={handleBack}
                aria-label="Go back"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>

            <Box>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
              >
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  color="text.secondary"
                  variant="body2"
                  onClick={() => navigate("/reports")}
                >
                  Reports
                </Link>
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight={600}
                >
                  GST Report
                </Typography>
              </Breadcrumbs>

              <Typography variant="subtitle1" fontWeight={700}>
                GST Report
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={2.2}>
              <DatePicker
                label="From Date"
                format="DD/MM/YYYY"
                value={fromDate}
                onChange={(v) => setFromDate(v)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={2.2}>
              <DatePicker
                label="To Date"
                format="DD/MM/YYYY"
                value={toDate}
                onChange={(v) => setToDate(v)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="GST Reports"
                value={gstReport}
                onChange={(e) => setGstReport(e.target.value)}
              >
                <MenuItem value="output">Output (Sales)</MenuItem>
                <MenuItem value="rcm-sales">RCM – Sales</MenuItem>
                <MenuItem value="input">Input (Purchase)</MenuItem>
                <MenuItem value="rcm-purchase">RCM – Purchase</MenuItem>
                {/* <MenuItem value="combined">Combined</MenuItem> */}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Report Type"
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
              >
                <MenuItem value="report">Report</MenuItem>
                <MenuItem value="json">JSONS</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Group By"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="customer">Customer Wise</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={1.6}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                onClick={handleLoad}
                sx={{
                  height: 40,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                Load
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 1.5, border: "1px solid #ccc" }}>
          {/* REPORT TITLE */}
          <Box
            sx={{
              textAlign: "center",
              mb: 1.5,
              borderBottom: "1px solid #ccc",
              pb: 1,
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1rem" }}>
              {clientInfo?.name}
            </Typography>

            <Typography sx={{ fontSize: "0.75rem" }}>
              {clientInfo?.address}, {clientInfo?.city}, {clientInfo?.state} -{" "}
              {clientInfo?.zipcode}
            </Typography>

            <Typography sx={{ fontSize: "0.75rem" }}>
              GSTIN: {clientInfo?.gstin} | PAN: {clientInfo?.pan}
            </Typography>

            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ mt: 1, fontSize: "0.85rem" }}
            >
              {getReportTitle()}
            </Typography>

            <Typography sx={{ fontSize: "0.75rem", mt: 0.5 }}>
              {getDateRange()}
            </Typography>

            <Typography sx={{ fontSize: "0.70rem", mt: 0.5 }}>
              Generated On: {dayjs().format("DD/MM/YYYY hh:mm A")}
            </Typography>
          </Box>

          {loading ? (
            <CircularProgress size={22} />
          ) : viewType === "json" ? (
            renderJSON()
          ) : (
            renderTable(Array.isArray(data) ? data : [])
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default GSTReport;
